import { useEffect, type ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import { useOrg } from '@/shared/org';
import {
  GRID_SETTING_KEYS,
  loadGridSettings,
  type GridSettings,
} from '@/shared/components/Grid/settings';
import {
  applyThemeColors,
  sanitizeThemeColors,
  upgradeGridColors,
  type ThemeColors,
} from './themeColors';
import { applyThemeLayout, sanitizeThemeLayout } from './themeLayout';

/**
 * Paints the whole site in the active organization's colours.
 *
 * This is the *outside a document* half of the feature. The document half
 * lives in `GridConfigProvider`, which shadows this palette while a sheet is
 * open and restores it on unmount through the undo closure `applyThemeColors`
 * returns. Both write the same `<style>` element on purpose: two injectors
 * competing for the same custom properties is what the old `applyGridColors`
 * did, and whichever ran last silently won.
 *
 * Consequently this provider is the ONLY writer for `documentId === null`;
 * `GridConfigProvider` deliberately no longer touches colours in that case.
 */

/* --------------------------------------------------------------- reading -- */

/**
 * Unwrap the stored envelope. Both `grid_settings.theme_colors` and the
 * `document_theme` RPC return `{ "custom": { … } }`, matching the shape the
 * grid's palette has always been stored in.
 */
function unwrapTheme(value: unknown): ThemeColors | null {
  const inner =
    value && typeof value === 'object' && !Array.isArray(value) && 'custom' in value
      ? (value as { custom: unknown }).custom
      : value;
  const colors = sanitizeThemeColors(inner);
  return Object.keys(colors).length > 0 ? colors : null;
}

/**
 * The theme a loaded settings bag describes, or `null` for "inject nothing and
 * let tokens.css decide".
 *
 * A tenant that never opened the new screen still has the twelve legacy
 * `grid_colors` values, so those are upgraded on read. The legacy row is left
 * untouched — a rollback is a code revert, not a data migration.
 */
export function themeFromSettings(settings: GridSettings): ThemeColors | null {
  const stored = unwrapTheme(settings[GRID_SETTING_KEYS.theme]);
  if (stored) return stored;
  const legacy = upgradeGridColors(settings[GRID_SETTING_KEYS.colors]);
  return Object.keys(legacy).length > 0 ? legacy : null;
}

/**
 * `public.document_theme(p_document)` — the owning organization's theme merged
 * with the document's own override, resolved server-side.
 *
 * It exists because `grid_settings_select` gates organization-wide rows on
 * `private.is_member(org_id)`: an outsider holding a share, and an anonymous
 * visitor on a public link, read *no* org-wide row and would otherwise see the
 * document painted in their own tenant's colours. The function is
 * `security definer` and returns colours and nothing else.
 *
 * The `Json` the RPC is typed to return is still run through
 * `sanitizeThemeColors`: the value is a jsonb blob an administrator wrote, so
 * the generated type says what shape Postgres promises, not what the row holds.
 */
export async function fetchDocumentTheme(documentId: string): Promise<ThemeColors | null> {
  try {
    const { data, error } = await supabase.rpc('document_theme', { p_document: documentId });
    return error ? null : unwrapTheme(data);
  } catch {
    return null;
  }
}

/* -------------------------------------------------------------- provider -- */

export function ThemeColorsProvider({ children }: { children: ReactNode }) {
  const org = useOrg();
  const orgId = org.status === 'ready' ? org.orgId : null;

  useEffect(() => {
    // Drop the previous tenant's palette before the next one resolves, so an
    // organization switch — or a sign-out — never leaves the outgoing colours
    // on screen while a request is in flight.
    applyThemeColors(null);
    applyThemeLayout(null);
    if (!orgId) return;

    let active = true;
    void (async () => {
      let colors: ThemeColors | null = null;
      let settings: GridSettings | null = null;
      try {
        settings = await loadGridSettings(orgId);
        colors = themeFromSettings(settings);
      } catch {
        colors = null;
      }
      if (active && colors) applyThemeColors(colors);
      // Sizes and grid lines ride with the palette; documents shared from another
      // organization keep the viewer's own.
      if (active && settings) applyThemeLayout(sanitizeThemeLayout(settings[GRID_SETTING_KEYS.layout]));
    })();

    return () => {
      active = false;
    };
  }, [orgId]);

  return <>{children}</>;
}
